import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { api, ROOM_BY_TYPE } from "@/lib/api";
import { ArrowLeft, Search as SearchIcon } from "lucide-react";
import RoomItem from "@/components/RoomItem";

/**
 * Room — per-media-type library view. The layout container adapts to the
 * room's storage metaphor:
 *   - vhs      → grid of VHS cases
 *   - spine    → book-shelf row (spines)
 *   - record   → record crate
 *   - card     → grid of index cards (articles)
 *   - recipe   → grid of recipe cards
 *   - polaroid → polaroid column wall
 * The header holds the search bar; typing filters live against `title`.
 */
export default function Room() {
  const { type } = useParams();
  const room = ROOM_BY_TYPE[type];
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!room) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/items", { params: { type } });
        if (!cancel) setItems(r.data || []);
      } catch (e) {
        console.error(`[room:${type}] load failed:`, e);
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [room, type]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(it => (it.title || "").toLowerCase().includes(needle));
  }, [items, q]);

  if (!room) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-10 pb-24" data-testid={`room-view-${type}`}>
      <Link to="/" data-testid="back-to-rooms" className="inline-flex items-center gap-2 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors">
        <ArrowLeft size={12} strokeWidth={1.5} />
        back to rooms
      </Link>

      <header className="text-center mt-8">
        <p className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--brass)] opacity-80">
          the {room.name.toLowerCase()} room
        </p>
        <h1 className="mt-3 font-serif-lit text-4xl sm:text-5xl text-[var(--cream)] leading-[1.05]">
          {room.name}
        </h1>
        <p className="mt-3 font-mono-lc text-[10px] tracking-[0.18em] uppercase text-[var(--cream-mute)]">
          {loading
            ? "…"
            : filtered.length === 1
              ? `1 of ${items.length}`
              : `${filtered.length} of ${items.length} ${room.copy}`}
        </p>
      </header>

      <div className="mt-8 room-search" data-testid="room-search">
        <input
          type="search"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder={`search ${room.name.toLowerCase()}…`}
          data-testid="room-search-input"
        />
        <SearchIcon size={14} strokeWidth={1.5} className="room-search-icon" />
      </div>

      {loading ? (
        <p className="mt-16 text-center font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)]" data-testid="room-loading">
          opening the room…
        </p>
      ) : filtered.length === 0 ? (
        <EmptyRoom room={room} hasItems={items.length > 0} />
      ) : (
        <RoomShelf metaphor={room.metaphor} items={filtered} />
      )}
    </div>
  );
}

function RoomShelf({ metaphor, items }) {
  if (metaphor === "spine") {
    return (
      <div className="mt-12 book-shelf" data-testid="room-shelf-books">
        {items.map(it => <RoomItem key={it.id} item={it} metaphor="spine" />)}
      </div>
    );
  }
  if (metaphor === "record") {
    return (
      <div className="mt-12 record-crate" data-testid="room-shelf-music">
        {items.map(it => <RoomItem key={it.id} item={it} metaphor="record" />)}
      </div>
    );
  }
  if (metaphor === "polaroid") {
    return (
      <div className="mt-12 photo-wall" data-testid="room-shelf-photos">
        {items.map(it => <RoomItem key={it.id} item={it} metaphor="polaroid" />)}
      </div>
    );
  }
  if (metaphor === "vhs") {
    return (
      <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5" data-testid="room-shelf-vhs">
        {items.map(it => <RoomItem key={it.id} item={it} metaphor="vhs" />)}
      </div>
    );
  }
  // card + recipe both use index-card grid
  const testid = metaphor === "recipe" ? "room-shelf-recipes" : "room-shelf-articles";
  return (
    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5" data-testid={testid}>
      {items.map(it => <RoomItem key={it.id} item={it} metaphor={metaphor} />)}
    </div>
  );
}

function EmptyRoom({ room, hasItems }) {
  return (
    <div className="mt-16 text-center" data-testid="room-empty">
      <p className="font-serif-lit italic text-lg text-[var(--cream-dim)]">
        {hasItems ? "nothing matches that search." : `no ${room.copy} yet.`}
      </p>
      {!hasItems && (
        <p className="mt-2 font-mono-lc text-[10px] tracking-[0.18em] uppercase text-[var(--cream-mute)]">
          tap the + to add your first
        </p>
      )}
    </div>
  );
}
